import { QuestionOutlineIcon, WarningTwoIcon } from '@chakra-ui/icons'
import type {
  FlexProps
} from '@chakra-ui/react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Grid,
  Image,
  Select,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure
} from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type {
  Item,
  Offer,
  OfferLeasing,
  OfferVehicle
} from 'BrokerTsLib/src/dtos'
import {
  CalculationStatus
} from 'BrokerTsLib/src/dtos'
import { Insurer, Product, Risk, Selection } from 'BrokerTsLib/src/enums'
import { useCustomToast } from 'BrokerTsLib/src/hooks/useCustomToast'
import React, { forwardRef, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'

import { useOffersVehicleContext } from '@/context/OffersVehicleProvider'
import { usePartner } from '@/context/PartnerContext'
import { usePriceFormat } from '@/hooks/usePriceFormat'
import { usePrintContext } from '@/context/PrintContext'

import { useAuth } from '@/context/AuthContext'
import { Apps } from '@/dtos'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useOffersAddonContext } from '../../context/OffersAddonProvider'
import { useGetRisks, useInsurerPaymentFrequencies } from '../../hooks'
import { useApp } from '../../hooks/useApp'
import { useConfirmOffers } from '../../hooks/useConfirmOffers'
import {
  useInvokeBmw,
  useSelectCalculation,
  useSetOfferPaymentFrequency
} from '../../hooks/useOffer'
import { useUpdateLastWizardStep } from '../../hooks/useWizard'
import refreshIcon from '../../images/refresh-icon.svg'
import warningIcon from '../../images/warning-icon-white.svg'
import { BmwPriceChangedDialog } from '../BmwPriceChangedDialog'
import { ConfirmErrorDialog } from '../ConfirmErrorDialog'
import { CustomConfirmDialog } from '../CustomConfirmDialog'
import { InsuranceType } from '../InsuranceQuoteItem/Summary/InsuranceType'
import { OfferPart } from '../InsuranceQuoteItem/Summary/OfferPart'
import { useLoadingOverlay } from '../LoadingOverlay'
import { OfferStatus } from '../QuoteItem'
import { BmwContinueMessageDialog } from '../Steps/BmwContinueMessageDialog'

interface SummaryItemProps {
  children?: React.ReactNode
  label: string | React.ReactNode
}

const SummaryItem = ({
  children,
  label,
  sx,
  ...rest
}: SummaryItemProps & FlexProps) => {
  return (
    <Flex
      sx={{
        width: '100%',
        h: 12,
        px: 5,
        py: 3,
        alignItems: 'center',
        justifyContent: 'space-between',
        bg: 'neutral.50',
        ...sx,
      }}
      {...rest}
    >
      <Box sx={{ fontSize: 'sm' }}>{label}</Box>

      {children && children}
    </Flex>
  )
}

const SummaryItemDivider = () => {
  return (
    <Divider
      sx={{
        width: 'calc(100% - 2rem)',
        mx: 'auto',
        mb: '-1px',
        borderBottomColor: 'white',
        opacity: 1,
        zIndex: 1,
      }}
    />
  )
}

interface QuoteItemSidebarProps {
  currentStatus: OfferStatus
  changedPaymentFrequencyId?: number
  onPaymentFrequencyChange: (paymentFrequencyId: number) => void
  offer: OfferLeasing | Offer
  submitHandler: (forceCalculation: boolean) => Promise<any> | void
  availableDiscounts: boolean
}

export const QuoteItemSidebar = forwardRef(
  (
    {
      currentStatus,
      changedPaymentFrequencyId,
      offer,
      submitHandler,
      onPaymentFrequencyChange,
      availableDiscounts,
    }: QuoteItemSidebarProps,
    _,
  ) => {
    const { translate, translateFormat } = useTranslationsContext()
    const { loading } = useLoadingOverlay()
    const { printMode } = usePrintContext()
    const { product, app } = useApp()
    const { currentPartner } = usePartner()
    const { priceFormat } = usePriceFormat()
    const { user } = useAuth()
    const isPublicPoleas = app == Apps.publicPoleas

    const { updateOffer } = useOffersVehicleContext()
    const { offers: addonOffers } = useOffersAddonContext()
    const { mutateAsync: updateStep } = useUpdateLastWizardStep()
    const { isPending: isLoadingUpdatePf } = useSetOfferPaymentFrequency()

    const {
      insuranceParamsId,
      refetchInsuranceParams,
      insuranceParams,
      isDisabled,
    } = useInsuranceParams()

    const allowSelectMultipleMainOffers = user?.allowMoreThanOneMainOffers ?? false
    const isSelected = insuranceParams?.generalParams.selectedOfferId == offer.offerId
    const showContinueButton = !isDisabled || product == Product.VehicleLeasingInsurance && !insuranceParams?.generalParams.contractNumber && !isSelected
    const activeGapOffer = addonOffers?.filter(x => x.riskId == Risk.GAP).find((x) => x.isActive)
    const isGapExtraOfferSelected = insuranceParams?.baseParams?.addonVariants?.find((x) => x.riskVariantId == activeGapOffer?.riskVariantId)?.selected ?? false

    const navigate = useNavigate()

    const {
      data: dataPf,
      isLoading: isLoadingPf,
      isFetching: isFetchingPf,
    } = useInsurerPaymentFrequencies()
    const { data: dataRisks } = useGetRisks()

    const { mutateAsync: invokeBmw } = useInvokeBmw()
    const { mutateAsync: selectOffer } = useSelectCalculation()

    const { isOpen: isOpenConfirmedOfferConfirmDialog, onOpen: onOpenConfirmedOfferConfirmDialog, onClose: onCloseConfirmedOfferConfirmDialog } = useDisclosure()
    const { isOpen: isOpenConfirmAlert, onOpen: openConfirmAlert, onClose: onCloseConfirmAlert } = useDisclosure()
    const { isOpen: isOpenBmwContinueMessage, onOpen: onOpenBmwContinueMessage, onClose: onCloseBmwContinueMessage } = useDisclosure()
    const { isOpen: isOpenLeasingContinueMessage, onOpen: onOpenLeasingContinueMessage, onClose: onCloseLeasingContinueMessage } = useDisclosure()
    const { isOpen: isOpenIncorectGapDialog, onOpen: onOpenIncorectGapDialog, onClose: onCloseIncorectGapDialog } = useDisclosure()

    const cancelRef = useRef<HTMLButtonElement>(null)
    const incorectGapDialogRef = useRef<HTMLButtonElement>(null)

    const {
      confirmOffers,
      confirmOffersOnly,
      isSubmitting,
      setIsSubmitting,
      setErrorMessages,
      errorDialogState,
      priceChangeDialogState,
    } = useConfirmOffers()

    const allGapOffers = addonOffers.filter((x) => x.riskId == Risk.GAP) ?? []
    const linkedOfferAddon = allGapOffers.find((x) =>
      x.linkedOfferId?.includes(offer.offerId),
    )

    const anyDiscount =
      offer.discounts &&
      offer.discounts.filter((x) => (x.discount ?? 0) > 0).length > 0

    const errorParts = offer.parts // .filter(x => ![Risk.POV, Risk.HAV, Risk.SPOL].includes(x.riskId)) // pokud nas zajimaji pouze doplnky
      .filter((x) => (x.errors?.length ?? 0) > 0)

    const errorPartsJsx =
      errorParts.map((x) => (
        <Flex direction="column" key={x.partId}>
          {x.errors?.map((e, i) => (
            <Text key={i}>
              {e.message ?? translate('quoteItem.sidebar.offerDoesNotMeetRequirements', 'Nabídka nemusí odpovídat požadovaným parametrům')}
            </Text>
          ))}
        </Flex>
      )) ?? []

    const anyPartError = errorParts.length > 0

    const isBmw = currentPartner?.partner === 'BMWFS' || currentPartner?.partner === 'BMWFSSK'
    if (isBmw && offer.underwriting)
      errorPartsJsx.push(
        <Flex direction="column" key={0}>
          <Text>
            {translate('quoteItem.sidebar.offerNeedsApproval', 'U této kalkulace je potřeba schválení ze strany pojišťovny')}
          </Text>
        </Flex>,
      )
    const showWarning = anyPartError || offer.underwriting

    const { catchNetworkErrorAndNotify } = useCustomToast()

    const handleSubmitOffer = () => {
      setIsSubmitting(true)
      console.log('handleSubmitOffer', isBmw)
      if (allowSelectMultipleMainOffers) {
        // nastaveni priznaku o vybrani offery
        offer.selected = true
        updateOffer(offer as OfferVehicle)

        setIsSubmitting(false)
        return
      }

      if (isBmw) {
        invokeBmw({
          action: 'Continue',
          offerId: offer.offerId,
          paramsId: insuranceParamsId!,
        }).catch((errs: any) => {
          const responseData = errs.response?.data ?? []

          if (responseData.length > 0)
            setErrorMessages(
              responseData.map((e: any) =>
                e.exception?.message ? e.exception.message : e.message,
              ),
            )

          setIsSubmitting(false)
        })
      } else {
        const nextPageTarget = isPublicPoleas ? '/poleas/wizard/3' : '/steps/3/' + insuranceParamsId

        if (isSelected) {
          navigate(nextPageTarget)
        } else {
          selectOffer({ paramsId: insuranceParamsId!, offerId: offer.offerId })
            .then(async () => {
              if ((insuranceParams?.generalParams.lastWizardStep ?? 2) == 2) {
                await updateStep({
                  paramsId: Number(insuranceParamsId!),
                  step: 3,
                })
                await refetchInsuranceParams()
              } else {
                await refetchInsuranceParams()
              }

              navigate(nextPageTarget)
            })
            .catch(catchNetworkErrorAndNotify)
            .finally(() => setIsSubmitting(false))
        }
      }
    }

    let allowedPf: Item[] = []
    if (dataPf && !isLoadingPf && !isFetchingPf) {
      allowedPf =
        dataPf.data.find((ipf) => ipf.insurerId == offer.insurerId)
          ?.paymentFrequencies ?? []
    }

    const getPaymentFrequency = (insurerId?: number, paymentFrequencyId?: number) => {
      return dataPf?.data
        .find((x) => x.insurerId == insurerId)
        ?.paymentFrequencies.find((x) => x.id == paymentFrequencyId)
    }

    const pf = getPaymentFrequency(offer.insurerId, offer.paymentFrequencyId)
    const paymentCount = pf?.value ? 12 / parseInt(pf.value) : 0
    const linkedAddonPricePerYear = linkedOfferAddon?.pricePerYear ?? 0
    const yearPrice = offer.price + (linkedOfferAddon?.pricePerYear ?? 0)

    const isInCalculationState = offer.state == CalculationStatus.Calculation


    const getTotalPrice = (offer: Offer) => {
      if (offer.priceFrequency)
        return offer.priceFrequency * paymentCount

      // importovane nemusi mit frekvenci. V takovem pripade zobrazim aspon importovanou price
      return offer.price + linkedAddonPricePerYear
    }

    const totalPrice = getTotalPrice(offer)

    // v pripade sjenani POV bez HAV spolu s GAP MAX se ma vyhodit dialog, ze to nelze
    const isIncorectGap = isGapExtraOfferSelected
      && activeGapOffer?.insurerId == Insurer.Fortegra
      && activeGapOffer?.productVariant == 'MAX'
      && !offer.parts.some(p => p.riskId == Risk.HAV)

    {/* handler na zruseni vyberu kalkulace ke sjednani. */ }
    const handleCancelSelection = useCallback(() => {
      // zruseni priznaku o vybrani offery
      offer.selected = false
      updateOffer(offer as OfferVehicle)
    }, [offer])

    return (
      <Flex
        flexDir="column"
        sx={{
          width: { md: '17rem' },
          alignItems: 'start',
          p: {
            base: 4,
            md: 0.5,
          },
        }}
      >
        <InsuranceType
          label={translate('paymentFrequency', 'Frekvence placení')}
          value={<Text sx={{ fontSize: 'sm' }}>#{offer.offerId}</Text>}
          valueSx={{ fontWeight: 'normal', pr: 4 }}
          valid={true}
        />
        <FormControl w="100%">
          <Select
            value={changedPaymentFrequencyId ?? offer.paymentFrequencyId}
            icon={isLoadingUpdatePf ? <Spinner /> : undefined}
            disabled={
              isDisabled ||
              loading ||
              product == Product.VehicleLeasingInsurance ||
              printMode
            }
            onChange={(e) => onPaymentFrequencyChange(parseInt(e.target.value))}
          >
            {allowedPf.map((item) => (
              <option value={item.id} key={item.id}>
                {item.description}
              </option>
            ))}
          </Select>
        </FormControl>

        {/* vyplni prazdne misto. Minimalni vysku zaridi hardcoded mezera */}
        <SummaryItem label="" flex={1} sx={{ py: 0 }}></SummaryItem>

        {!isPublicPoleas &&
          <>
            <SummaryItem label={translate('discounts', 'Slevy')}>
              <Flex sx={{ gap: 0 }} flexDirection="column">
                {/* {(isLoadingDiscounts || isRunnigDiscountDelay) && <Spinner />} */}
                {anyDiscount &&
                  offer.discounts
                    .filter((x) => (x.discount ?? 0) > 0 && (x.editK2 || x.showK2))
                    .map((x) => (
                      <Text
                        key={x.insuranceCategoryDiscountId}
                        sx={{ fontSize: 'sm', fontWeight: 'semibold' }}
                      >
                        {x.name} - {x.discount}
                        {x.unit}
                      </Text>
                    ))}

                {!anyDiscount && (
                  <Text sx={{ fontSize: 'md', fontWeight: 'semibold' }}>
                    {availableDiscounts ? translate('notEntered', 'Nezadáno') : translate('notAvailable', 'Není k dispozici')}
                  </Text>
                )}
              </Flex>
            </SummaryItem>

            <SummaryItemDivider />
          </>
        }

        <SummaryItem label={translate('payment', 'Splátka')}>
          <Text sx={{ fontSize: 'md', fontWeight: 'semibold' }}>
            {priceFormat(offer.priceFrequency)}
          </Text>
        </SummaryItem>

        <SummaryItemDivider />

        <SummaryItem
          label={
            <Flex gap={2} alignItems="center">
              {translate('totalYearly', 'Celkem / rok')}{' '}
              {totalPrice > 0 &&
                yearPrice > 0 &&
                parseInt(totalPrice.toString()) != parseInt(yearPrice.toString()) &&
                offer.paymentFrequencyId != Selection.Yearly && (
                  <Tooltip
                    hasArrow
                    label={
                      <Stack>
                        <Text>
                          {translate('yearlyPremium', 'Roční pojistné')}:{' '}
                          <strong>{priceFormat(yearPrice)}</strong>
                        </Text>
                        <Text>
                          {translate('totalPrice', 'Cena celkem')}:{' '}
                          <strong>{priceFormat(totalPrice)}</strong> (
                          {paymentCount} * {priceFormat(offer.priceFrequency)})
                        </Text>
                        <Text>
                          {translate('priceDifference', 'Rozdíl v ceně')}:{' '}
                          <strong>{priceFormat(totalPrice - yearPrice)}</strong>
                        </Text>
                      </Stack>
                    }
                    placement="top"
                  >
                    <WarningTwoIcon />
                  </Tooltip>
                )}
            </Flex>
          }
        >
          <Text sx={{ fontSize: '2xl', fontWeight: 'bold' }}>
            {priceFormat(
              currentStatus === OfferStatus.error ? 0 : totalPrice,
              currentStatus === OfferStatus.error ? 'N/A' : undefined,
            )}
          </Text>
        </SummaryItem>

        {linkedOfferAddon && (
          <>
            <SummaryItemDivider />

            <SummaryItem
              label={
                <Flex alignItems="center">
                  <Text size="xs" mr={1}>
                    {linkedOfferAddon.name}
                  </Text>
                  <Tooltip
                    hasArrow
                    label={`Splátka ${priceFormat(linkedOfferAddon.priceFrequency)} (${getPaymentFrequency(linkedOfferAddon.insurerId, linkedOfferAddon.paymentFrequencyId)?.description?.toLowerCase()})`}
                    placement="top"
                  >
                    <QuestionOutlineIcon />
                  </Tooltip>
                </Flex>
              }
            >
              <Text sx={{ fontSize: 'md', fontWeight: 'semibold' }}>
                {priceFormat(linkedOfferAddon.pricePerYear)}
              </Text>
            </SummaryItem>
          </>
        )}

        <Grid sx={{ w: '100%', mt: 0.5 }}>
          {showContinueButton && (
            <>
              {(currentStatus === OfferStatus.changed ||
                loading ||
                currentStatus === OfferStatus.error) && (
                  <Button
                    variant="custom"
                    colorScheme={
                      currentStatus === OfferStatus.error ? 'red' : 'primary'
                    }
                    size="lg"
                    isLoading={loading}
                    loadingText={translate('calculating', 'Kalkuluji') + '...'}
                    onClick={() => {
                      if (
                        !isInCalculationState &&
                        [
                          Insurer.Allianz,
                          Insurer.GeneraliCeskaPojistovna,
                        ].includes(offer.insurerId)
                      )
                        onOpenConfirmedOfferConfirmDialog()
                      else submitHandler(true)
                    }}
                  >
                    {translate('recalculate', 'Přepočítat')}
                    <Image
                      src={refreshIcon}
                      alt={translate('recalculate', 'Přepočítat')}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 3,
                        transform: 'translateY(-50%)',
                      }}
                    />
                  </Button>
                )}

              {currentStatus === OfferStatus.completed &&
                !loading &&
                !printMode && (
                  <CustomTooltip
                    label={errorPartsJsx.length > 0 ? errorPartsJsx : undefined}
                    trigger="clickHover"
                    placement="top"
                    hasArrow
                    onClick={
                      errorPartsJsx.length > 0
                        ? (e) => e.stopPropagation()
                        : undefined
                    }
                  >
                    {offer.selected &&
                      <Button
                        variant="custom"
                        colorScheme="blue"
                        size="lg"
                        type="submit"
                        width={'100%'}
                        isLoading={isSubmitting}
                        onClick={handleCancelSelection}
                      >
                        {translate('cancelSelection', 'Zrušit výběr')}
                      </Button>
                    }

                    {!offer.selected &&
                      <Button
                        variant="custom"
                        colorScheme={allowSelectMultipleMainOffers ? 'blue' : 'green'}
                        size="lg"
                        type="submit"
                        width={'100%'}
                        isLoading={isSubmitting}
                        onClick={() => {
                          if (isIncorectGap) onOpenIncorectGapDialog()
                          else if (anyPartError) openConfirmAlert()
                          else if (allowSelectMultipleMainOffers) handleSubmitOffer()
                          else if (offer.underwriting) openConfirmAlert()
                          // klasicky wizard pro leasing - v takovem pripade se ma pouze confirmovat a vybrat na insurance
                          else if (app == Apps.leasing) onOpenLeasingContinueMessage()
                          // confirmace + redirect do bmw
                          else if (isBmw) onOpenBmwContinueMessage()
                          // klasicky vyber nabidky + redirect na K3
                          else handleSubmitOffer()
                        }}
                      >
                        {app == Apps.leasing || allowSelectMultipleMainOffers ? translate('select', 'Vybrat') : translate('continue', 'Pokračovat')}

                        {showWarning && (
                          <>
                            <Image
                              src={warningIcon}
                              alt={translate('warning', 'varování').toLocaleLowerCase()}
                              onClick={(e) => e.preventDefault()}
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                right: 3,
                                transform: 'translateY(-50%)',
                              }}
                            />

                            <AlertDialog
                              leastDestructiveRef={cancelRef}
                              isOpen={isOpenConfirmAlert}
                              onClose={onCloseConfirmAlert}
                              isCentered
                            >
                              <AlertDialogOverlay>
                                <AlertDialogContent>
                                  <AlertDialogHeader
                                    fontSize="lg"
                                    fontWeight="bold"
                                  >
                                    {translate('warning', 'varování').toLocaleLowerCase()}
                                  </AlertDialogHeader>

                                  {anyPartError && (
                                    <AlertDialogBody>
                                      {translate('quoteItem.sidebar.offerDoesNotMatch', 'U Vámi zvolené nabídky evidujeme následující nesrovnalosti') + ':'}
                                      <br /> <br />
                                      {errorParts.map((x) => {
                                        const name =
                                          dataRisks?.data.find(
                                            (r) => r.id == x.riskId,
                                          )?.name ??
                                          x.name ??
                                          ''

                                        return (
                                          <Box key={x.name} sx={{ ml: -2 }}>
                                            <OfferPart
                                              {...x}
                                              insurer={offer.insurerId}
                                              label={name}
                                            />
                                          </Box>
                                        )
                                      })}
                                      <br /> <br />
                                      {translate('quoteItem.sidebar.continueAnyways', 'Opravdu chcete pokračovat?')}
                                    </AlertDialogBody>
                                  )}

                                  {offer.underwriting && (
                                    <AlertDialogBody>
                                      {translate('quoteItem.sidebar.underwriting.needsApproval', 'U této kalkulace je potřeba schválení ze strany pojišťovny.')}
                                      <br />
                                      {translate('quoteItem.sidebar.underwriting.calculationId', 'ID kalkulace pro komunikaci s pojišťovnou:')}{' '}
                                      <strong>
                                        {offer.internalCalculationId}
                                      </strong>
                                      .
                                      <br />
                                      <br />
                                      {translate('quoteItem.sidebar.underwriting.alreadyApproved', 'Mám již kalkulaci schválenou?')}
                                    </AlertDialogBody>
                                  )}

                                  <AlertDialogFooter>
                                    <Button
                                      variant="solid"
                                      colorScheme="gray"
                                      onClick={onCloseConfirmAlert}
                                      ref={cancelRef}
                                    >
                                      {translate('no', 'Ne')}
                                    </Button>
                                    <Button
                                      variant="solid"
                                      colorScheme="green"
                                      color="white"
                                      onClick={() => {
                                        if (isBmw)
                                          onOpenBmwContinueMessage()
                                        else
                                          handleSubmitOffer()

                                        if (isOpenConfirmAlert)
                                          onCloseConfirmAlert()
                                      }}
                                      ml={3}
                                    >
                                      {translate('yes', 'Ano')}
                                    </Button>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialogOverlay>
                            </AlertDialog>
                          </>
                        )}

                        <BmwPriceChangedDialog
                          isOpen={priceChangeDialogState.isOpen}
                          priceError={priceChangeDialogState.message}
                          onClose={priceChangeDialogState.onClose}
                          onConfirm={priceChangeDialogState.onConfirm}
                        />

                        <BmwContinueMessageDialog
                          isOpen={isOpenBmwContinueMessage}
                          onClose={onCloseBmwContinueMessage}
                          selectedOffers={[offer]}
                          onConfirm={() => confirmOffers([offer], handleSubmitOffer)}
                        />

                        <CustomConfirmDialog
                          isOpen={isOpenLeasingContinueMessage}
                          onClose={onCloseLeasingContinueMessage}
                          title={translate('warning', 'Upozornění')}
                          yesButton={{
                            text: translate('understand', 'Rozumím'),
                            onClick: () => {
                              confirmOffersOnly([offer])
                              onCloseLeasingContinueMessage()
                            },
                          }}
                          noButton={{
                            text: translate('cancel', 'Zrušit'),
                            onClick: onCloseLeasingContinueMessage,
                          }}
                        >
                          <Stack>
                            <Text>{translate('quoteItem.sidebar.leasing.selectOfferDialogMessage', 'Kliknutím na \'Rozumím\' uložíte vybranou kalkulaci u pojišťovny.')}</Text>
                          </Stack>
                        </CustomConfirmDialog>
                      </Button>
                    }
                  </CustomTooltip>
                )}
            </>
          )}
        </Grid>

        <ConfirmErrorDialog
          isOpen={errorDialogState.isOpen}
          errorMessages={errorDialogState.messages}
          onClose={errorDialogState.onClose}
        />

        <CustomConfirmDialog
          isOpen={isOpenConfirmedOfferConfirmDialog}
          onClose={onCloseConfirmedOfferConfirmDialog}
          title={translate('warning', 'Upozornění')}
          yesButton={{
            onClick(e) {
              submitHandler(true)
              onCloseConfirmedOfferConfirmDialog()
            },
          }}
          noButton={{
            onClick(e) {
              submitHandler(false)
              onCloseConfirmedOfferConfirmDialog()
            },
          }}
        >
          <Stack gap={2}>
            <Text>{translate('quoteItem.sidebar.confirmOfferDialogMessage', 'Opravdu chcete upravit garantovanou nabídku?')}</Text>
            <Text>{translate('quoteItem.sidebar.confirmOfferDialogMessage2', 'Jakýkoliv přepočet již nemusí držet původní výši pojistného.')}</Text>
          </Stack>
        </CustomConfirmDialog>

        <AlertDialog
          leastDestructiveRef={incorectGapDialogRef}
          isOpen={isOpenIncorectGapDialog}
          onClose={onCloseIncorectGapDialog}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {translate('quoteItem.sidebar.cannotContractOffer', 'Nabídku nelze sjednat')}
              </AlertDialogHeader>

              <AlertDialogBody>
                {translateFormat('quoteItem.sidebar.cannotContractOfferMessage', `Nabídku nelze sjednat s vybranou variantou GAP připojištění (${activeGapOffer?.name}).`, { activeGapOfferName: activeGapOffer?.name })}
                <br /><br />
                {translate('quoteItem.sidebar.cannotContractOfferMessage2', 'Upravte prosím variantu GAP připojištění.')}
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button colorScheme="primary" onClick={onCloseIncorectGapDialog}>
                  {translate('close', 'Zavřít')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Flex>
    )
  },
)
